/**
 * Avatar configuration for Koshmi platform
 * Provides fun, educational-themed avatars for students and teachers
 */

export interface AvatarOption {
  emoji: string;
  label: string;
  category: 'student' | 'teacher' | 'both';
}

export const AVATAR_COLORS = [
  { value: '#EF4444', label: 'Red' },
  { value: '#F97316', label: 'Orange' },
  { value: '#EAB308', label: 'Yellow' },
  { value: '#22C55E', label: 'Green' },
  { value: '#3B82F6', label: 'Blue' },
  { value: '#6366F1', label: 'Indigo' },
  { value: '#A855F7', label: 'Purple' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#14B8A6', label: 'Teal' },
  { value: '#06B6D4', label: 'Cyan' },
];

export const AVATAR_EMOJIS: AvatarOption[] = [
  // Student-focused avatars
  { emoji: '🎓', label: 'Graduate', category: 'student' },
  { emoji: '📚', label: 'Book Lover', category: 'student' },
  { emoji: '🔬', label: 'Scientist', category: 'student' },
  { emoji: '🎨', label: 'Artist', category: 'student' },
  { emoji: '🎵', label: 'Musician', category: 'student' },
  { emoji: '🏃', label: 'Athlete', category: 'student' },
  { emoji: '🚀', label: 'Explorer', category: 'student' },
  { emoji: '🦸', label: 'Hero', category: 'student' },
  { emoji: '🐱', label: 'Cat Lover', category: 'student' },
  { emoji: '🐶', label: 'Dog Lover', category: 'student' },
  { emoji: '🦄', label: 'Unicorn', category: 'student' },
  { emoji: '🌟', label: 'Star', category: 'student' },
  { emoji: '🌈', label: 'Rainbow', category: 'student' },
  { emoji: '🎮', label: 'Gamer', category: 'student' },
  { emoji: '🤖', label: 'Robot', category: 'student' },

  // Teacher-focused avatars
  { emoji: '👨‍🏫', label: 'Teacher', category: 'teacher' },
  { emoji: '📖', label: 'Educator', category: 'teacher' },
  { emoji: '🧑‍🔬', label: 'Professor', category: 'teacher' },
  { emoji: '🎯', label: 'Mentor', category: 'teacher' },
  { emoji: '💡', label: 'Innovator', category: 'teacher' },
  { emoji: '🏆', label: 'Champion', category: 'teacher' },
  { emoji: '🌍', label: 'Explorer', category: 'teacher' },
  { emoji: '🎭', label: 'Creative', category: 'teacher' },
  { emoji: '☕', label: 'Coffee Lover', category: 'teacher' },
  { emoji: '🦉', label: 'Wise Owl', category: 'teacher' },
  { emoji: '🌺', label: 'Nature Lover', category: 'teacher' },
  { emoji: '📐', label: 'Math Wizard', category: 'teacher' },
  { emoji: '🔭', label: 'Science Guru', category: 'teacher' },
  { emoji: '✏️', label: 'Writer', category: 'teacher' },

  // Universal avatars
  { emoji: '😊', label: 'Happy', category: 'both' },
  { emoji: '😎', label: 'Cool', category: 'both' },
  { emoji: '🤓', label: 'Nerdy', category: 'both' },
  { emoji: '🥳', label: 'Party', category: 'both' },
  { emoji: '🌻', label: 'Sunflower', category: 'both' },
  { emoji: '⭐', label: 'Superstar', category: 'both' },
];

/**
 * Get avatars filtered by role
 */
export const getAvatarsByRole = (role: 'student' | 'teacher'): AvatarOption[] => {
  return AVATAR_EMOJIS.filter((avatar) => avatar.category === role || avatar.category === 'both');
};

/**
 * Get default avatar based on role
 */
export const getDefaultAvatar = (role: 'student' | 'teacher') => {
  if (role === 'student') {
    return {
      emoji: '🎓',
      color: '#3B82F6', // Blue
    };
  } else {
    return {
      emoji: '👨‍🏫',
      color: '#6366F1', // Indigo
    };
  }
};

/**
 * Get a random avatar for a role
 */
export const getRandomAvatar = (role: 'student' | 'teacher') => {
  const avatars = getAvatarsByRole(role);
  const randomEmoji = avatars[Math.floor(Math.random() * avatars.length)];
  const randomColor = AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)];

  return {
    emoji: randomEmoji.emoji,
    color: randomColor.value,
  };
};
